import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation, useNavigation } from "react-router-dom";

import { LinearProgress } from "@mui/material";
import {
  getIsLoading,
  getIsShowModuleSidebar,
} from "../../../application/selectors/ui";
import { getMenuStatus, getUserMenuList } from "../../../application/selectors/user";
import { resolveCanonicalMenuPath } from "../../pages/SLN/shared/projectWorkspace/projectWorkspaceMenu";
import { setUserMenuList } from "../../../application/slices/user";
import { onCheckIsShowMainMenu } from "../../middleware/middleware";
import { getAuthSlice } from "../../../application/selectors/auth";
import { getAccessToken } from "../../../infrastructure/utils/authSession";
import {
  startAuthTokenKeepAlive,
  stopAuthTokenKeepAlive,
} from "../../../infrastructure/utils/authTokenKeepAlive";
import AppBarContainer from "./AppBar";
import ModuleSideBar from "./ModuleSideBar";
import ProfileCompletionAlert from "./ProfileCompletionAlert";
import ProtectedOutlet from "./ProtectedOutlet";
import SideBarContainer from "./SideBar";

export default function Layout() {
  const { pathname, search, hash } = useLocation();
  const { state } = useNavigation();
  const dispatch = useDispatch();

  const isShowModuleSideBar = useSelector(getIsShowModuleSidebar);
  const isLoading = useSelector(getIsLoading);
  const menuStatus = useSelector(getMenuStatus);
  const menuList = useSelector(getUserMenuList);

  const [open, setOpen] = useState(false);
  const [isShowMainMenu, setIsShowMainMenu] = useState(true);
  const main = useRef();

  const { isLoggedIn, user } = useSelector(getAuthSlice);
  const menuUserKey = user
    ? `${user.id ?? user.Id ?? ""}-${user.roleId ?? user.RoleId ?? ""}`
    : null;
  const lastMenuUserKey = useRef(null);

  useEffect(() => {
    if (!isLoggedIn || !getAccessToken(user) || !menuUserKey) {
      lastMenuUserKey.current = null;
      return;
    }

    if (lastMenuUserKey.current === menuUserKey && menuStatus !== "idle") {
      return;
    }

    if (menuStatus !== "idle") {
      return;
    }

    lastMenuUserKey.current = menuUserKey;
    dispatch(setUserMenuList());
  }, [dispatch, isLoggedIn, menuUserKey, menuStatus]);

  useEffect(() => {
    if (!isLoggedIn) {
      stopAuthTokenKeepAlive();
      return undefined;
    }

    startAuthTokenKeepAlive();
    return () => {
      stopAuthTokenKeepAlive();
    };
  }, [isLoggedIn, user?.accessToken]);

  useEffect(() => {
    setIsShowMainMenu(onCheckIsShowMainMenu());
  }, []);

  useEffect(() => {
    if (main.current) {
      window.scrollTo(0, 0);
      main.current.scrollTo(0, 0);
    }
  }, [pathname]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const canonicalPath =
    menuStatus === "loaded" && menuList?.length
      ? resolveCanonicalMenuPath(pathname, menuList)
      : pathname;

  if (canonicalPath !== pathname) {
    return (
      <Navigate
        to={{ pathname: canonicalPath, search, hash }}
        replace
      />
    );
  }

  return (
    <Box sx={{ display: "flex" }} className="app-layout-container">
      <CssBaseline />

      <AppBarContainer
        open={open}
        handleDrawerClose={handleDrawerClose}
        handleDrawerOpen={handleDrawerOpen}
        isShowMainMenu={isShowMainMenu}
      />

      <Box
        component="main"
        data-testid="dashboard-root"
        className={clsx({
          "app-content-container": true,
          "full-width": !isShowModuleSideBar,
          "hide-main-menu": !isShowMainMenu,
        })}
        ref={main}
      >
        <ProfileCompletionAlert />
        {(isLoading || state === "loading") && (
          <Box sx={{ width: "100%", position: "absolute", top: 0, left: 0 }}>
            <LinearProgress />
          </Box>
        )}

        <SideBarContainer
          open={open}
          setOpen={setOpen}
          isShowMainMenu={isShowMainMenu}
        />

        <ModuleSideBar isShowMainMenu={isShowMainMenu} />

        <ProtectedOutlet />
      </Box>
    </Box>
  );
}
